import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";

type Gesture = { start: number; from: number; last: number; t: number; v: number; moved: boolean };

/**
 * Bottom sheet over the map. `peek` always shows; dragging it up (or tapping it) reveals the rest.
 * Publishes the visible peek height as `--peek` on the parent so the map can stop above it.
 */
export function Sheet({ label, peek, children }: { label: string; peek: ReactNode; children?: ReactNode }) {
  const sheet = useRef<HTMLElement>(null);
  const peekEl = useRef<HTMLDivElement>(null);
  const safe = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState({ sheet: 0, peek: 0, safe: 0 });
  const [open, setOpen] = useState(false);
  const [drag, setDrag] = useState<number | null>(null);
  const [animate, setAnimate] = useState(false);
  const gesture = useRef<Gesture | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const next = { sheet: sheet.current!.offsetHeight, peek: peekEl.current!.offsetHeight, safe: safe.current!.offsetHeight };
      sheet.current!.parentElement?.style.setProperty("--peek", `${next.peek + next.safe}px`);
      setSize(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sheet.current!);
    ro.observe(peekEl.current!);
    // Only animate once the first closed position is painted, so the page doesn't open with a slide.
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => {
      ro.disconnect();
      cancelAnimationFrame(id);
    };
  }, []);

  const closedY = Math.max(0, size.sheet - size.peek - size.safe);
  const y = drag ?? (open ? 0 : closedY);
  const expandable = closedY > 0;

  // Escape closes the sheet, like a dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (!expandable || e.button !== 0) return;
    // Controls inside the peek (the blue check, its dialog) keep their own taps.
    if ((e.target as Element).closest("a, button:not(.sheet-handle), dialog")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    gesture.current = { start: e.clientY, from: y, last: e.clientY, t: e.timeStamp, v: 0, moved: false };
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g) return;
    const dy = e.clientY - g.start;
    if (!g.moved && Math.abs(dy) < 6) return;
    g.moved = true;
    const dt = e.timeStamp - g.t;
    if (dt > 0) g.v = (e.clientY - g.last) / dt;
    g.last = e.clientY;
    g.t = e.timeStamp;
    // Past either end the sheet follows the finger at a third of the speed.
    const to = g.from + dy;
    setDrag(to < 0 ? to / 3 : to > closedY ? closedY + (to - closedY) / 3 : to);
  };
  const up = (e: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (!g.moved) return setOpen((o) => !o);
    const v = e.timeStamp - g.t > 100 ? 0 : g.v; // a pause before letting go cancels the fling
    setOpen(Math.abs(v) > 0.4 ? v < 0 : y < closedY / 2);
    setDrag(null);
  };

  return (
    <section
      ref={sheet}
      className={`sheet${animate && drag === null ? " sheet-animate" : ""}`}
      aria-label={label}
      style={{ transform: `translateY(${y}px)` }}
    >
      <div ref={peekEl} className={`sheet-peek${expandable ? " sheet-grab" : ""}`} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        {expandable && (
          <button
            type="button"
            className="sheet-handle"
            aria-expanded={open}
            aria-label={open ? "Mostrar menos" : "Mostrar mais"}
            // Pointer taps toggle through the peek's handler; this catches Enter/Space only.
            onClick={(e) => e.detail === 0 && setOpen((o) => !o)}
          >
            <span />
          </button>
        )}
        {peek}
      </div>
      <div className="sheet-rest" inert={!open}>
        {children}
      </div>
      <span ref={safe} className="sheet-safe" aria-hidden="true" />
    </section>
  );
}
